/**
 * Sprint 2 CP8 — TierResolver.
 *
 * <p>Função PURA que decide o tier de execução do player a partir do
 * {@link SyncResponse}. Espelha a lógica de
 * {@code MediaSyncService.resolvePreviewTier} (Java) — backend é a fonte
 * de verdade, mas o client valida defensivamente.</p>
 *
 * <h3>Hierarquia (decisão #14 do PLAYLIST_FEATURE_PLAN.md)</h3>
 * <ol>
 *   <li><b>Tier 1</b> — Grupo Exclusivo: backend NÃO envia slot_sequence
 *       (player.exclusiveGroupId domina via {@code files[]}).</li>
 *   <li><b>Tier 2</b> — Playlist Exclusiva: slot_sequence presente com
 *       {@code tier=2}.</li>
 *   <li><b>Tier 3</b> — Playlist Normal: slot_sequence presente com
 *       {@code tier=3}.</li>
 *   <li><b>Tier 4</b> — Atribuição Direta (legado): nenhuma das anteriores.
 *       Player toca {@code files[]} em loop como sempre.</li>
 * </ol>
 *
 * <p><b>Decisão de design:</b> o resolver preferencia o {@code tier}
 * declarado no slot_sequence (backend é canônico). Se o response trouxer
 * slot_sequence sem o campo tier (rollback de versão / payload corrompido),
 * cai pra Tier 4 — fail-safe.</p>
 */

import type { SlotSequenceDto } from '~/api/types/sync';
import type { Tier } from './types';

/**
 * Resolve o tier a partir do {@code slot_sequence} do sync response.
 * Aceita null/undefined (sem slot_sequence = Tier 1 ou 4, colapsados
 * pro player como "tocar files[] em loop").
 */
export function resolveTier(
  slotSequence: SlotSequenceDto | null | undefined,
): Tier {
  if (!slotSequence) {
    // Sem slot_sequence: Tier 1 (Grupo Exclusivo) ou Tier 4 (Direto legado).
    // Do ponto de vista do player ambos são "tocar files[] em loop", então
    // colapsamos pra Tier 4. O backend distingue via outros campos
    // (não relevante pro player consumer).
    return 4;
  }
  if (slotSequence.tier === 2) return 2;
  if (slotSequence.tier === 3) return 3;
  // Fallback defensivo: payload com slot_sequence mas tier desconhecido →
  // Tier 4 (toca files[] legado). Não jogamos exception porque sync é
  // caminho crítico.
  return 4;
}
